/**
 * Spread Checkpoint Service
 *
 * Handles fetching and decoding spread checkpoint data from the API.
 * Each checkpoint contains metadata and signed URLs that point to protobuf-encoded data.
 */

import {
  ProtobufDecodeError,
  fetchAndDecodeCached,
  getElementTypeName,
  initializeDecoder,
} from '@/lib/protobuf/decoder'
import { checkpointsService } from './checkpoints.service'
import { elementsService } from './elements.service'
import { logger } from '@/utils/logger'
import { logProtobufCheckpointDebug } from '@/lib/debug/protobuf-checkpoint-debug'
import { logSpreadXmlDebug } from '@/lib/debug/spread-xml-debug'
import {
  DecodedElement,
  SignedElementReference,
  SpreadCheckpoint,
  SpreadCheckpointMetadata,
} from '@/api/types/spread-checkpoint'

function deepClone<T>(value: T): T {
  const sc = (globalThis as unknown as { structuredClone?: (v: unknown) => unknown })
    .structuredClone

  if (typeof sc === 'function') {
    return sc(value) as T
  }

  return JSON.parse(JSON.stringify(value)) as T
}

function stableElementCacheKey(reference: SignedElementReference, spreadId?: string): string {
  // Composite key: ${spreadId}:${objectId}:${versionId}
  // Matches the backend spec's recommended cache key format.
  const prefix = spreadId ? `${spreadId}:` : ''

  return `${prefix}${reference.objectId}:${reference.versionId}`
}

function parseKindId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)

    return Number.isFinite(n) ? n : null
  }

  return null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function getElementDataByObjectId(response: unknown, objectId: string): unknown | undefined {
  const record = asRecord(response)

  if (!record) return undefined
  return objectId in record ? record[objectId] : undefined
}

function getElementArrayFromResponse(response: unknown): unknown[] | null {
  const topLevel = asArray(response) // Your current API shape.

  if (topLevel) return topLevel

  const direct = asRecord(response)

  if (!direct) return null

  // Keep a minimal fallback if backend wraps the array.
  const wrapped = asArray(direct.elements) ?? asArray(direct.items)

  return wrapped ?? null
}

/**
 * Extract all signed element references from the checkpoint metadata.
 * Handles both UserLayer and MasterLayer, and captures "pending" elements
 * (versionId = "pending", signedUrl = null) that need API hydration.
 */
function extractElementReferences(metadata: SpreadCheckpointMetadata): SignedElementReference[] {
  const elements: SignedElementReference[] = []
  const seenIds = new Set<string>()

  function extractFromObject(
    obj: unknown,
    targetLayer?: 'userLayer' | 'masterLayer',
    path = ''
  ): void {
    if (!obj || typeof obj !== 'object') {
      return
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        extractFromObject(item, targetLayer, `${path}[${index}]`)
      })
      return
    }

    const record = obj as Record<string, unknown>

    const objectId = record.objectId || record.ObjectId || record['objectId'] || record['ObjectId']
    const kindIdRaw = record.kindId || record.KindId || record['kindId'] || record['KindId']
    const kindId = parseKindId(kindIdRaw)

    // Accept elements with OR without a signedUrl (pending elements have null/undefined signedUrl)
    if (typeof objectId === 'string' && typeof kindId === 'number') {
      if (seenIds.has(objectId as string)) {
        return
      }

      seenIds.add(objectId as string)

      const signedUrlRaw =
        record.signedUrl || record.SignedUrl || record['signedUrl'] || record['SignedUrl']
      const signedUrl = typeof signedUrlRaw === 'string' ? signedUrlRaw : null

      const versionId =
        record.versionId || record.VersionId || record['versionId'] || record['VersionId'] || ''
      const signedExpiration =
        record.signedExpiration ||
        record.SignedExpiration ||
        record['signedExpiration'] ||
        record['SignedExpiration'] ||
        ''

      elements.push({
        objectId: objectId as string,
        versionId: versionId as string,
        kindId: kindId as number,
        signedUrl,
        signedExpiration: signedExpiration as string,
        targetLayer,
        elements:
          record.elements || record.Elements
            ? extractElementReferences({
                nsDocument: record.elements || record.Elements,
              })
            : undefined,
      })
    }

    Object.entries(record).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        extractFromObject(value, targetLayer, path ? `${path}.${key}` : key)
      }
    })
  }

  const nsDoc = metadata.nsDocument || metadata.NsDocument

  if (nsDoc) {
    const nsDocObj = nsDoc as Record<string, unknown>
    const pasteboardElements = nsDocObj.pasteboardElements || nsDocObj.PasteboardElements

    if (pasteboardElements) {
      const pbObj = pasteboardElements as Record<string, unknown>

      // Extract from both UserLayer and MasterLayer
      const layerKeys: Array<{
        key: string
        name: 'userLayer' | 'masterLayer'
      }> = [
        { key: 'userLayer', name: 'userLayer' },
        { key: 'UserLayer', name: 'userLayer' },
        { key: 'masterLayer', name: 'masterLayer' },
        { key: 'MasterLayer', name: 'masterLayer' },
      ]

      let foundAnyLayer = false

      for (const { key, name } of layerKeys) {
        const layer = pbObj[key]

        if (!layer || typeof layer !== 'object') continue

        foundAnyLayer = true
        const layerObj = layer as Record<string, unknown>
        const elementsArray = layerObj.elements || layerObj.Elements

        if (Array.isArray(elementsArray)) {
          elementsArray.forEach((element) => {
            extractFromObject(element, name)
          })
        }
      }

      if (!foundAnyLayer) {
        extractFromObject(nsDoc)
      }
    } else {
      extractFromObject(nsDoc)
    }
  }

  return elements
}

/**
 * Decode a single element from its signed URL.
 * S3 contains the specific element type (RectangleElement, TextElement, etc.), not StowedElement.
 */
async function decodeElement(
  reference: SignedElementReference,
  spreadId?: string
): Promise<DecodedElement> {
  try {
    const cacheKey = stableElementCacheKey(reference, spreadId)
    const typeName = getElementTypeName(reference.kindId)

    if (!reference.signedUrl) {
      throw new Error(
        `Element ${reference.objectId} has no signedUrl (pending element - use API hydration)`
      )
    }

    // Cache by stable key so signed URL refreshes don't force refetch/redecode.
    const data = await fetchAndDecodeCached(typeName, reference.signedUrl, cacheKey)

    return {
      reference,
      data,
      decodedAt: new Date().toISOString(),
    }
  } catch (error) {
    logger.error(`Failed to decode element ${reference.objectId}:`, error)
    const errorMessage = error instanceof Error ? error.message : String(error)

    throw new ProtobufDecodeError(
      `Failed to decode element ${reference.objectId}: ${errorMessage}`,
      getElementTypeName(reference.kindId),
      error
    )
  }
}

/**
 * Check if a reference points to a pending element
 * (backend hasn't synced to S3 yet - data lives in Redis cache).
 */
function isPendingElement(ref: SignedElementReference): boolean {
  return ref.versionId === 'pending' || !ref.signedUrl
}

/**
 * Fetch pending elements from the API (those not yet synced to S3).
 * Groups by target layer for efficient batch requests.
 */
async function fetchPendingElements(
  spreadId: string,
  pendingRefs: SignedElementReference[]
): Promise<DecodedElement[]> {
  if (pendingRefs.length === 0) return []

  const byLayer = new Map<string, SignedElementReference[]>()

  for (const ref of pendingRefs) {
    const layer = ref.targetLayer ?? 'userLayer'

    if (!byLayer.has(layer)) byLayer.set(layer, [])
    byLayer.get(layer)!.push(ref)
  }

  const results: DecodedElement[] = []

  for (const [layer, refs] of byLayer) {
    try {
      const objectIds = refs.map((r) => r.objectId)
      const response = await elementsService.getElementsByIds(
        spreadId,
        layer as 'userLayer' | 'masterLayer',
        objectIds
      )
      const responseArray = getElementArrayFromResponse(response)

      for (let index = 0; index < refs.length; index += 1) {
        const ref = refs[index]
        // Primary: objectId lookup (map shape).
        // Fallback: array responses that return elements in requested ID order.
        const data =
          getElementDataByObjectId(response, ref.objectId) ??
          (responseArray && index < responseArray.length ? responseArray[index] : undefined)

        if (data != null) {
          results.push({
            reference: ref,
            data,
            decodedAt: new Date().toISOString(),
          })
        }
      }
    } catch (err) {
      logger.warn(`Failed to fetch pending elements for layer ${layer}:`, err)
    }
  }

  return results
}

/**
 * Decode all elements in parallel (with concurrency limit).
 * Splits references into synced (S3) and pending (API) paths.
 */
async function decodeElements(
  references: SignedElementReference[],
  concurrency = 5,
  spreadId?: string
): Promise<DecodedElement[]> {
  const syncedRefs = references.filter((r) => !isPendingElement(r))
  const pendingRefs = references.filter((r) => isPendingElement(r))

  const results: DecodedElement[] = []
  const errors: Array<{ reference: SignedElementReference; error: Error }> = []

  // In-flight cache: stableKey -> decode promise
  const decodePromiseByStableKey = new Map<string, Promise<DecodedElement>>()

  const decodeWithDedupe = (ref: SignedElementReference): Promise<DecodedElement> => {
    const key = stableElementCacheKey(ref, spreadId)
    const existing = decodePromiseByStableKey.get(key)

    if (existing) return existing

    const promise = decodeElement(ref, spreadId).catch((err) => {
      decodePromiseByStableKey.delete(key)
      throw err
    })

    decodePromiseByStableKey.set(key, promise)
    return promise
  }

  // Decode synced elements from S3 (batched with concurrency limit)
  const s3Promise = (async () => {
    for (let i = 0; i < syncedRefs.length; i += concurrency) {
      const batch = syncedRefs.slice(i, i + concurrency)
      const batchResults = await Promise.allSettled(batch.map((ref) => decodeWithDedupe(ref)))

      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          results.push(result.value)
        } else {
          const reference = batch[index]

          errors.push({ reference, error: result.reason })
          logger.warn(`Failed to decode element ${reference.objectId}:`, result.reason)
        }
      })
    }
  })()

  // Fetch pending elements from API in parallel
  const pendingPromise = spreadId
    ? fetchPendingElements(spreadId, pendingRefs).then((pending) => results.push(...pending))
    : Promise.resolve()

  await Promise.all([s3Promise, pendingPromise])

  if (errors.length > 0) {
    logger.warn(`Failed to decode ${errors.length} out of ${references.length} elements`)
  }

  return results
}

/**
 * Inject decoded data back into metadata structure where signed URLs are found.
 * Elements are matched by objectId (primary) or by their own signedUrl (fallback).
 */
function injectDecodedDataIntoMetadata(
  metadata: SpreadCheckpointMetadata,
  decodedElements: DecodedElement[]
): SpreadCheckpointMetadata {
  // Create a map for quick lookup: objectId -> decoded data (for elements)
  const decodedDataMap = new Map<string, unknown>()

  decodedElements.forEach((element) => {
    decodedDataMap.set(element.reference.objectId, element.data)
  })

  // Create a map for signedUrl -> decoded data (fallback for objects without a matching objectId)
  const signedUrlToDataMap = new Map<string, unknown>()

  decodedElements.forEach((element) => {
    if (element.reference.signedUrl) {
      signedUrlToDataMap.set(element.reference.signedUrl, element.data)
    }
  })

  // Deep clone the metadata to avoid mutating the original
  const enrichedMetadata = deepClone(metadata) as SpreadCheckpointMetadata

  // Recursively inject decoded data where signedUrl is found
  function injectIntoObject(obj: unknown): void {
    if (!obj || typeof obj !== 'object') {
      return
    }

    if (Array.isArray(obj)) {
      obj.forEach((item) => injectIntoObject(item))
      return
    }

    const record = obj as Record<string, unknown>
    const objectId = record.objectId || record.ObjectId || record['objectId'] || record['ObjectId']
    const signedUrl =
      record.signedUrl || record.SignedUrl || record['signedUrl'] || record['SignedUrl']

    // Inject decoded data by objectId (works for both synced and pending elements)
    // or by signedUrl (fallback for main checkpoint or other objects).
    if (typeof objectId === 'string' || typeof signedUrl === 'string') {
      let decodedData: unknown | undefined

      if (typeof objectId === 'string') {
        decodedData = decodedDataMap.get(objectId)
      }

      if (decodedData === undefined && typeof signedUrl === 'string') {
        decodedData = signedUrlToDataMap.get(signedUrl)
      }

      if (decodedData !== undefined) {
        record.decodedData = decodedData
      }
    }

    // Recursively process nested objects
    Object.values(record).forEach((value) => {
      if (value && typeof value === 'object') {
        injectIntoObject(value)
      }
    })
  }

  // Start injection from the root metadata
  injectIntoObject(enrichedMetadata)

  return enrichedMetadata
}

/**
 * Get spread checkpoint by ID
 */
export async function getSpreadCheckpoint(
  spreadId: string,
  options?: { decodeElements?: boolean; decodeElementsLimit?: number }
): Promise<SpreadCheckpoint> {
  try {
    const shouldDecodeElements = options?.decodeElements ?? true
    const decodeElementsLimit = options?.decodeElementsLimit

    // Ensure decoder is initialized
    await initializeDecoder()

    const metadata = (await checkpointsService.getById(spreadId)) as SpreadCheckpointMetadata

    // Extract all element references
    const elementReferences = extractElementReferences(metadata)

    // Decode all elements. The API is the sole source of checkpoint metadata/document
    // structure (metadata.nsDocument) - only individual elements are fetched from S3,
    // using the signed URLs the API already returned alongside that metadata.
    const elements = shouldDecodeElements
      ? await decodeElements(
          typeof decodeElementsLimit === 'number'
            ? elementReferences.slice(0, Math.max(0, decodeElementsLimit))
            : elementReferences,
          5,
          spreadId
        )
      : []

    // Inject decoded element data back into metadata
    const enrichedMetadata = injectDecodedDataIntoMetadata(metadata, elements)

    const spreadCheckpoint: SpreadCheckpoint = {
      metadata: enrichedMetadata, // Use enriched metadata instead of original
      elementReferences,
      elements,
      decodedAt: new Date().toISOString(),
    }

    logProtobufCheckpointDebug(spreadId, spreadCheckpoint)
    logSpreadXmlDebug(spreadId, spreadCheckpoint)

    return spreadCheckpoint
  } catch (error) {
    logger.error(`Failed to get spread checkpoint ${spreadId}:`, error)
    throw error
  }
}

/**
 * Decode a single element reference on-demand.
 * This is useful when `decodeElements` is disabled/limited during initial load.
 */
export async function decodeElementReference(
  reference: SignedElementReference
): Promise<DecodedElement> {
  await initializeDecoder()
  return decodeElement(reference)
}

/**
 * Spread checkpoint service
 */
export const spreadCheckpointService = {
  getSpreadCheckpoint,
  decodeElementReference,
}
